import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { Session, User, AuthError } from '@supabase/supabase-js';
import { supabase } from '../lib/supabaseClient';
import { UserRole } from '../types/profile';
import { ensureMyProfile } from '../services/profileService';

// User metadata passed during sign-up
interface SignUpMetadata {
  display_name: string;
  avatar_url?: string;
}

// Auth context type
interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  role: UserRole | null;
  refreshRole: () => Promise<void>;
  signIn: (email: string, password: string) => Promise<{ error: AuthError | null }>;
  signUp: (email: string, password: string, metadata: SignUpMetadata) => Promise<{ error: AuthError | null; user: User | null }>;
  signOut: () => Promise<void>;
  uploadAvatar: (userId: string, file: File) => Promise<{ url: string | null; error: string | null }>;
  updateUserMetadata: (data: Record<string, string>) => Promise<{ error: AuthError | null }>;
}

// Create context with default values
const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  loading: true,
  role: null,
  refreshRole: async () => {},
  signIn: async () => ({ error: null }),
  signUp: async () => ({ error: null, user: null }),
  signOut: async () => {},
  uploadAvatar: async () => ({ url: null, error: null }),
  updateUserMetadata: async () => ({ error: null }),
});

// Custom hook
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [role, setRole] = useState<UserRole | null>(null);

  // Fetch the role from the profiles table for a given user
  const fetchRole = useCallback(async (userId: string) => {
    const { data } = await supabase
      .from('profiles')
      .select('role')
      .eq('user_id', userId)
      .maybeSingle<{ role: UserRole }>();
    setRole(data?.role ?? null);
  }, []);

  // Expose so components can re-read role after changes (e.g. admin panel)
  const refreshRole = useCallback(async () => {
    const { data: { user: currentUser } } = await supabase.auth.getUser();
    if (currentUser) {
      await fetchRole(currentUser.id);
    }
  }, [fetchRole]);

  useEffect(() => {
    // Get the initial session
    const getInitialSession = async () => {
      try {
        const { data: { session: currentSession } } = await supabase.auth.getSession();
        setSession(currentSession);
        setUser(currentSession?.user ?? null);
        if (currentSession?.user) {
          await fetchRole(currentSession.user.id);
        }
      } catch (error) {
        console.error('Error fetching initial session:', error);
      } finally {
        setLoading(false);
      }
    };

    getInitialSession();

    // Listen for auth state changes
    // NOTE: Supabase does NOT await async callbacks — keep this synchronous and
    // fire async work as detached promises so setLoading(false) always runs.
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, newSession) => {
        setSession(newSession);
        setUser(newSession?.user ?? null);
        setLoading(false);
        if (newSession?.user) {
          // Always ensure a profiles row exists — idempotent, safe to call on every sign-in.
          // This covers immediate sign-ups (no email confirm) AND email-confirmed sign-ups.
          if (event === 'SIGNED_IN') {
            ensureMyProfile(newSession.user).catch((err) =>
              console.error('ensureMyProfile error:', err)
            );
          }
          fetchRole(newSession.user.id).catch(() => setRole(null));
        } else {
          setRole(null);
        }
      }
    );

    // Cleanup subscription on unmount
    return () => {
      subscription.unsubscribe();
    };
  }, [fetchRole]);

  const signIn = useCallback(async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error };
  }, []);

  const signUp = useCallback(async (email: string, password: string, metadata: SignUpMetadata) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          display_name: metadata.display_name,
          avatar_url: metadata.avatar_url || '',
        },
      },
    });
    return { error, user: data?.user ?? null };
  }, []);

  const uploadAvatar = useCallback(async (userId: string, file: File) => {
    // Create a unique file path: avatars/{userId}/{timestamp}.{ext}
    const fileExt = file.name.split('.').pop()?.toLowerCase() || 'png';
    const filePath = `${userId}/${Date.now()}.${fileExt}`;

    const { error: uploadError } = await supabase.storage
      .from('avatars')
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: true,
      });

    if (uploadError) {
      return { url: null, error: uploadError.message };
    }

    // Get the public URL
    const { data: urlData } = supabase.storage
      .from('avatars')
      .getPublicUrl(filePath);

    return { url: urlData.publicUrl, error: null };
  }, []);

  const updateUserMetadata = useCallback(async (data: Record<string, string>) => {
    const { error } = await supabase.auth.updateUser({ data });
    return { error };
  }, []);

  const signOut = useCallback(async () => {
    const { error } = await supabase.auth.signOut();
    if (error) {
      console.error('Error signing out:', error);
    }
  }, []);

  const value: AuthContextType = {
    user,
    session,
    loading,
    role,
    refreshRole,
    signIn,
    signUp,
    signOut,
    uploadAvatar,
    updateUserMetadata,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
